import * as def from '@/definitions'
import { TextInfo } from '@/utilityModels/textInfo'
import { Layer } from './layer';
import { ExTextLayer } from './exTextLayer';
import { ArrayHelper } from '@/helper/arrayHelper';
import { StringHelper } from '@/helper/stringHelper';
import { UuidHelper } from '@/helper/uuidHelper';

// 通常文字レイヤー
export class PlainTextLayer extends ExTextLayer
{
    public get layerType() : string { return "plainText"; }

    // 自動(文字を枠に合わせたサイズにする)
    private _autoSize : boolean = false;
    public get autoSize() : boolean { return this._autoSize; }
    public set autoSize(autoSize : boolean) {this._autoSize = autoSize; }

    // 自動サイズで計算されたフォントサイズを保持する
    public savedFontSize: number = 0;

    // 自動折り返し(ある程度で折り返す)
    private _autoNewLine: boolean = false;
    public get autoNewLine(): boolean { return this._autoNewLine; }
    public set autoNewLine(autoNewLine: boolean) {
        this.savedFontSize = 0;
        this._autoNewLine = autoNewLine;
    }

    // 行間(%)
    private _lineSpacing: number = 120;
    public get lineSpacing(): number { return this._lineSpacing; }
    public set lineSpacing(lineSpacing) {
        this.savedFontSize = 0;
        this._lineSpacing = lineSpacing;
    }

    // 文字方向
    // 0: 横書き
    // 1: 縦書き
    private _textOrientation: number = 0;
    public get textOrientation(): number { return this._textOrientation; }
    public set textOrientation(textOrientation: number) {
        this.savedFontSize = 0;
        this._textOrientation = textOrientation;
    }

    // 水平揃え
    // 0: 左
    // 1: 中央
    // 2: 右
    // 3: 均等
    private _horizontalAlignment : number = 0;
    public get horizontalAlignment() : number { return this._horizontalAlignment; }
    public set horizontalAlignment(horizontalAlignment : number) {
        this.savedFontSize = 0;
        this._horizontalAlignment = horizontalAlignment;
    }

    // 垂直揃え
    // 0: 上
    // 1: 中央
    // 2: 下
    private _verticalAlignment : number = 0;
    public get verticalAlignment() : number { return this._verticalAlignment; }
    public set verticalAlignment(verticalAlignment : number) { this._verticalAlignment = verticalAlignment; }

    // 語頭追加
    private _prefix: string = "";
    public get prefix(): string { return this._prefix; }
    public set prefix(prefix: string) {
        this.savedFontSize = 0;
        this._prefix = prefix;
    }

    // 語尾追加
    private _suffix: string = "";
    public get suffix(): string { return this._suffix; }
    public set suffix(suffix: string) {
        this.savedFontSize = 0;
        this._suffix = suffix;
    }

    // @override
    public get text(): string {
        // setterと一緒に定義する必要がある
        return super.text;
    }
    public set text(text: string) {
        this.savedFontSize = 0;
        super.text = text;
    }

    // @override
    public get width(): number {
        return super.width;
    }
    public set width(width: number) {
        this.savedFontSize = 0;
        super.width = width;
    }

    // @override
    public get height(): number {
        return super.height;
    }
    public set height(height: number) {
        this.savedFontSize = 0;
        super.height = height;
    }

    // @override
    public get textInfoArray(): TextInfo[] {
        return super.textInfoArray;
    }
    public set textInfoArray(textInfoArray: TextInfo[]) {
        this.savedFontSize = 0;
        super.textInfoArray = textInfoArray;
    }

    public replaceTextInfo(textInfo, index)
    {
        this.savedFontSize = 0;
        super.replaceTextInfo(textInfo, index);
    }

    // 語頭語尾を含めたテキストを行ごとに分けて得る
    public textAll(placeholder: boolean) {
        let lines: any[][] = [];
        let line: any[] = [];

        // 語頭
        const pn = this.getPrefixCount()
        for (let i = 0; i < pn; i++) {
            line.push(this.getPrefixCharacterInfo(i));
        }

        // 本文
        const ln = this.getLineCount(placeholder);
        for (let l = 0; l < ln; l++) {
            const cn = this.getCharacterCount(l, placeholder);
            for (let c = 0; c < cn; c++) {
                line.push(this.getCharacterInfo(l, c, placeholder));
            }
            // 最終行以外
            if (l < ln - 1) {
                // 行を追加して初期化（改行）
                lines.push(line);
                line = [];
            }
        }

        // 語尾
        const sn = this.getSuffixCount();
        for (let i = 0; i < sn; i++) {
            line.push(this.getSuffixCharacterInfo(i));
        }

        // 最終行は語尾を追加してから加える
        lines.push(line);

        return lines;
    }

    // 語頭語尾を含めたテキストを得る
    // public get textAll(): string {
    //     return `${this.prefix}${this.text}${this.suffix}`;
    // }

    // 語頭の文字数取得(改行除く)
    public getPrefixCount()
    {
        var prefix = this.prefix.replace(/\r?\n/g, "");
        return prefix.length;
    }

    // 語尾の文字数取得(改行除く)
    public getSuffixCount()
    {
        var suffix = this.suffix.replace(/\r?\n/g, "");
        return suffix.length;
    }

    // 語頭の文字位置を元にして以下を取得
    // character : [文字]
    // index : トータル文字番号
    // textInfo : [TextInfo]
    public getPrefixCharacterInfo(index : number)
    {
        var prefix = this.prefix.replace(/\r?\n/g, "");
        if(0 <= index && index < prefix.length)
        {
            // 文法情報は先頭のものがあればそれを、なければ基本情報を取得
            return {
                character : prefix.substr(index, 1),
                index : index,
                textInfo : this.textInfoArray.length > 0 ? this.textInfoArray[0] : this.baseTextInfo
            };
        }

        return null;
    }

    // 語尾の文字位置を元にして以下を取得
    // character : [文字]
    // index : トータル文字番号
    // textInfo : [TextInfo]
    public getSuffixCharacterInfo(index : number)
    {
        var suffix = this.suffix.replace(/\r?\n/g, "");
        if(0 <= index && index < suffix.length)
        {
            // 文法情報は終端のものがあればそれを、なければ基本情報を取得
            return {
                character : suffix.substr(index, 1),
                index : index,
                textInfo : this.textInfoArray.length > 0 ? this.textInfoArray[this.textInfoArray.length - 1] : this.baseTextInfo
            };
        }

        return null;
    }

    // プロフィールキー
    private _profileKey : string = "";
    public get profileKey() { return this._profileKey; }
    public set profileKey(profileKey) { this._profileKey = profileKey; }

    // 作成
    public static create(text : string, x : number, y : number, width : number, height : number) : PlainTextLayer
    {
        var layer = new PlainTextLayer();
        layer.text = text;

        layer.autoSize = true;
        layer.autoNewLine = false;

        layer.horizontalAlignment = 0;
        layer.verticalAlignment = 0;
        layer.x = x;
        layer.y = y;
        layer.width = width;
        layer.height = height;
        layer.insertionId = UuidHelper.getUuid();

        // 初期文法設定
        layer.setWholeTextInfo(new TextInfo());

        return layer;
    }

    // 複製
    public clone()
    {
        var cloned = new PlainTextLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : PlainTextLayer)
    {
        layer.autoSize = this._autoSize;
        layer.autoNewLine = this._autoNewLine;
        layer.lineSpacing = this._lineSpacing;
        layer.textOrientation = this._textOrientation;
        layer.horizontalAlignment = this._horizontalAlignment;
        layer.verticalAlignment = this._verticalAlignment;
        layer.prefix = this._prefix;
        layer.suffix = this._suffix;
        layer.profileKey = this._profileKey;

        super.cloneParameter(layer);

        layer.savedFontSize = this.savedFontSize;
    }


    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.autoSize = this._autoSize;
        jsonObject.savedFontSize = this.savedFontSize;
        jsonObject.autoNewLine = this._autoNewLine;
        jsonObject.lineSpacing = this._lineSpacing;
        jsonObject.textOrientation = this._textOrientation;
        jsonObject.horizontalAlignment = this._horizontalAlignment;
        jsonObject.verticalAlignment = this._verticalAlignment;
        jsonObject.prefix = this._prefix;
        jsonObject.suffix = this._suffix;
        jsonObject.profileKey = this._profileKey;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : PlainTextLayer
    {
        var layer = new PlainTextLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.autoSize != null) { this._autoSize = jsonData.autoSize; }
        if(jsonData.autoNewLine != null) { this._autoNewLine = jsonData.autoNewLine; }
        if(jsonData.lineSpacing != null) { this._lineSpacing = jsonData.lineSpacing; }
        if(jsonData.textOrientation != null) { this._textOrientation = jsonData.textOrientation; }
        if(jsonData.horizontalAlignment != null) { this._horizontalAlignment = jsonData.horizontalAlignment; }
        if(jsonData.verticalAlignment != null) { this._verticalAlignment = jsonData.verticalAlignment; }
        if(jsonData.prefix != null) { this._prefix = jsonData.prefix; }
        if(jsonData.suffix != null) { this._suffix = jsonData.suffix; }
        if(jsonData.profileKey != null) { this._profileKey = jsonData.profileKey; }

        super.loadParameterFromJson(jsonData);

        if(jsonData.savedFontSize != null) { this.savedFontSize = jsonData.savedFontSize; }
    }
}
